import React from "react";
import { Controller } from "react-hook-form";
import { Dialog, Form } from "@sscs/ui";
import {
  ImportableVersion,
  ImportableVersionsSelection,
  getVersionsSelectionSchema,
} from "Modules/importableVersions";
import * as yup from "Utils/yup";
import { ImportExternalPackageRequest } from "../../types";

export type VersionsForm = Required<
  Pick<
    ImportExternalPackageRequest,
    "versions" | "notify_on_completion" | "auto_sync"
  >
>;

export const getVersionsSchema = (
  versions: ImportableVersion[],
  remainingCapacity: number,
  maxVersions: number,
): yup.SchemaOf<VersionsForm> =>
  yup.object({
    versions: getVersionsSelectionSchema(
      versions,
      remainingCapacity,
      maxVersions,
    ),
    notify_on_completion: yup.boolean().required(),
    auto_sync: yup.boolean().required(),
  });

type VersionsProps = {
  catalogName: string;
  versions: ImportableVersion[];
  remainingCapacity: number;
  maxVersions: number;
  autoApprovalEnabled: boolean;
};

const Versions = ({
  catalogName,
  versions,
  remainingCapacity,
  maxVersions,
  autoApprovalEnabled,
}: VersionsProps): React.JSX.Element => (
  <>
    <Controller<VersionsForm, "versions">
      name="versions"
      render={({ field, fieldState }) => (
        <>
          <ImportableVersionsSelection
            catalogName={catalogName}
            versions={versions}
            selected={field.value}
            onChange={field.onChange}
            remainingCapacity={remainingCapacity}
            maxVersions={maxVersions}
            emptySubject="versions"
          />
          {fieldState.error && (
            <Form.HelperText error>{fieldState.error.message}</Form.HelperText>
          )}
        </>
      )}
    />
    <Dialog.Section>
      <Dialog.Section.Title>Import options</Dialog.Section.Title>
      <Dialog.Section.Content>
        <Form.Checkbox
          name="notify_on_completion"
          label="Notify me when analysis completes"
          helperText="Get a notification once every selected version has finished analysis."
        />
        <Form.Checkbox
          name="auto_sync"
          label="Automatically sync new versions"
          helperText={`New versions are imported automatically as they become available. The package keeps at most ${maxVersions} versions; the oldest are removed once the limit is reached.`}
        />
        {!autoApprovalEnabled && (
          <Dialog.Banner type="warning" title="Auto approval is disabled">
            At least one version must be approved before the package appears in
            the catalog.
          </Dialog.Banner>
        )}
      </Dialog.Section.Content>
    </Dialog.Section>
  </>
);

export default Versions;
